/**
 * Medidas de la imagen que NO usan el OCR.
 *
 * Existe porque la legibilidad para una persona y el éxito del OCR son cosas
 * distintas, y las vimos separarse en las dos direcciones: el espécimen 2020 es
 * legible a ojo y el OCR no devolvió un dato; una captura de webcam fuera de
 * foco entregó el MRZ entero y de ahí una persona no lee ni el nombre.
 *
 * Todo acá es puro: entra una matriz de píxeles, sale un número. Se prueba sin
 * navegador, que es lo que permite calibrar umbrales sin levantar nada.
 */
import { MedidasDeZona, Rectangulo } from "./types";
import { pxPorMm } from "./tuning";

export interface ImagenPlana {
  /** RGBA, cuatro bytes por píxel, como lo devuelve getImageData. */
  data: Uint8ClampedArray | number[];
  width: number;
  height: number;
}

function luma(d: ImagenPlana["data"], i: number): number {
  return (d[i] * 299 + d[i + 1] * 587 + d[i + 2] * 114) / 1000;
}

function recorteValido(img: ImagenPlana, r?: Rectangulo): Rectangulo {
  if (!r) return { x: 0, y: 0, w: img.width, h: img.height };
  const x = Math.max(0, Math.min(img.width - 1, Math.round(r.x)));
  const y = Math.max(0, Math.min(img.height - 1, Math.round(r.y)));
  return {
    x,
    y,
    w: Math.max(1, Math.min(img.width - x, Math.round(r.w))),
    h: Math.max(1, Math.min(img.height - y, Math.round(r.h))),
  };
}

/** Filas de luma del recorte, que es sobre lo que trabajan todas las medidas. */
function filasDeLuma(img: ImagenPlana, r: Rectangulo): number[][] {
  const filas: number[][] = [];
  for (let y = r.y; y < r.y + r.h; y++) {
    const fila = new Array<number>(r.w);
    for (let x = 0; x < r.w; x++) fila[x] = luma(img.data, ((y * img.width) + r.x + x) * 4);
    filas.push(fila);
  }
  return filas;
}

/**
 * Desenfoque, de 0 (nítida) a 1 (papilla).
 *
 * Se vuelve a desenfocar la imagen y se mide cuánto cambió la variación entre
 * píxeles vecinos: una imagen nítida pierde mucho, una ya borrosa casi no
 * cambia. Es el método de Crete et al., y lo elegimos después de descartar la
 * varianza del laplaciano, que es la medida obvia y acá estaba mal:
 *
 *   2026-frente.jpg tiene varianza del laplaciano 29,5 —valor de imagen
 *   arruinada— y se lee entera a simple vista. Es blanda, no ilegible. Con un
 *   umbral sobre la varianza la habríamos rechazado, que es exactamente el
 *   error que esta capa existe para no cometer.
 *
 * Esta medida no depende del contraste ni de la resolución, así que los valores
 * de dos fotos distintas se pueden comparar entre sí.
 *
 * MEDIDO: 2020-Frente 0,18 · wikipedia 0,21 · 2026-frente 0,49, y sube de forma
 * monótona al desenfocar a propósito (σ1, σ2, σ4).
 */
export function desenfoque(img: ImagenPlana, rect?: Rectangulo): number {
  const r = recorteValido(img, rect);
  if (r.w < 10) return 1;
  const filas = filasDeLuma(img, r);

  const VENTANA = 9;
  const mitad = Math.floor(VENTANA / 2);
  let sumaOriginal = 0;
  let sumaPerdida = 0;

  for (const fila of filas) {
    // Desenfoque horizontal por media móvil, con suma corrida.
    const blur = new Array<number>(r.w);
    let acum = 0;
    for (let x = 0; x < r.w; x++) {
      acum += fila[x];
      if (x >= VENTANA) acum -= fila[x - VENTANA];
      const centro = x - mitad;
      if (centro >= 0) {
        const n = Math.min(VENTANA, x + 1);
        blur[centro] = acum / n;
      }
    }
    for (let x = r.w - mitad; x < r.w; x++) blur[x] = fila[x];

    for (let x = 1; x < r.w; x++) {
      const dOriginal = Math.abs(fila[x] - fila[x - 1]);
      const dBorrosa = Math.abs(blur[x] - blur[x - 1]);
      sumaOriginal += dOriginal;
      const perdida = dOriginal - dBorrosa;
      if (perdida > 0) sumaPerdida += perdida;
    }
  }

  if (sumaOriginal === 0) return 1;
  return (sumaOriginal - sumaPerdida) / sumaOriginal;
}

/**
 * Reflejo: fracción de teselas CLARAS y SIN DETALLE.
 *
 * Contar píxeles claros no sirve, y lo medimos: el frente del espécimen 2020 da
 * 36% de píxeles quemados y el dorso 50%, y no tienen ni un reflejo — es el
 * papel blanco del documento. Un documento es mayormente claro por definición.
 *
 * Lo que distingue al brillo sobre el plastificado es que además borra el
 * detalle: el papel blanco tiene texto encima, la mancha de reflejo no tiene
 * nada. Por eso se mide por teselas y se pide las dos condiciones.
 */
export function reflejo(img: ImagenPlana, rect?: Rectangulo, tesela = 24): number {
  const r = recorteValido(img, rect);
  if (r.w < tesela || r.h < tesela) return 0;
  const filas = filasDeLuma(img, r);

  let total = 0;
  let cegadas = 0;
  for (let ty = 0; ty + tesela <= r.h; ty += tesela) {
    for (let tx = 0; tx + tesela <= r.w; tx += tesela) {
      let suma = 0;
      let variacion = 0;
      for (let y = ty; y < ty + tesela; y++) {
        for (let x = tx; x < tx + tesela; x++) {
          suma += filas[y][x];
          if (x > tx) variacion += Math.abs(filas[y][x] - filas[y][x - 1]);
        }
      }
      const n = tesela * tesela;
      total++;
      if (suma / n >= 250 && variacion / n < 2) cegadas++;
    }
  }
  return total > 0 ? cegadas / total : 0;
}

/** Fracción de píxeles hundidos en sombra. */
export function oscuridad(img: ImagenPlana, rect?: Rectangulo): number {
  const r = recorteValido(img, rect);
  let oscuros = 0;
  let n = 0;
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) {
      if (luma(img.data, (y * img.width + x) * 4) <= 10) oscuros++;
      n++;
    }
  }
  return n > 0 ? oscuros / n : 0;
}

/**
 * Alto del renglón impreso, por geometría y sin OCR.
 *
 * Ubicada la tarjeta sabemos cuántos píxeles mide un milímetro, y el catálogo
 * dice cuánto mide el renglón de esa zona en el documento real.
 *
 * Es la medida que le falta al desenfoque para poder decidir: 2026-frente tiene
 * desenfoque 0,49 —peor que una foto buena desenfocada a propósito— y se lee,
 * porque su texto es grande. Lo que decide la legibilidad es el desenfoque
 * RELATIVO al tamaño del texto, y el tamaño del texto sale de acá.
 *
 * Devuelve null si no se ubicó la tarjeta: ahí no hay escala y no se afirma nada.
 */
export function altoDeRenglonPx(
  anchoTarjetaPx: number | null,
  altoNominalMm: number
): number | null {
  if (!anchoTarjetaPx || anchoTarjetaPx <= 0) return null;
  return pxPorMm(anchoTarjetaPx) * altoNominalMm;
}

export interface OpcionesDeMedicion {
  /** Ancho de la tarjeta ya enderezada. null si no se pudo ubicar. */
  anchoTarjetaPx?: number | null;
  /** Alto del renglón de esta zona en el documento real, en milímetros. */
  altoRenglonMm?: number;
}

export function medirZona(
  img: ImagenPlana,
  rect?: Rectangulo,
  opciones: OpcionesDeMedicion = {}
): MedidasDeZona {
  const r = recorteValido(img, rect);
  return {
    anchoPx: r.w,
    altoPx: r.h,
    desenfoque: desenfoque(img, r),
    reflejo: reflejo(img, r),
    oscuridad: oscuridad(img, r),
    altoRenglonPx:
      opciones.altoRenglonMm != null
        ? altoDeRenglonPx(opciones.anchoTarjetaPx ?? null, opciones.altoRenglonMm)
        : null,
  };
}
